import React, { useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { format, isValid, parse, parseISO } from "date-fns";
import { enUS } from "date-fns/locale";
import { dateLocale } from "i18n";

const ALREADY_IN_RECORD = "Already in your record";
const SEXES = ["male", "female", "other"];
const SHOWN_ALLERGY_TYPES = ["drug", "food", "environmental"];
const SHOWN_CONDITION_STATUSES = ["controlled", "resolved"];

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

/**
 * healthRecordImportService builds `current` and `proposed` as English display
 * strings, and they may come back from a saved pendingDocumentImport, so they are
 * left as they are and translated here. Only the parts the service writes itself
 * (sex, dates, allergy type, condition status, a course's dates) are swapped;
 * everything else is the document's own text, and anything not recognized is
 * shown unchanged.
 */
const useProposalText = () => {
  const { t } = useTranslation("masterchart");
  const locale = dateLocale();

  const isoDate = (value) => {
    const date = parseISO(String(value || ""));
    return isValid(date) ? date : null;
  };
  const longDate = (date) => format(date, t("importReview.dateFormat"), { locale });
  const day = (date) => format(date, t("importReview.dayFormat"), { locale });

  const demographic = (field, text) => {
    if (!text) return text;
    if (field === "sex") {
      const sex = String(text).toLowerCase();
      return SEXES.includes(sex) ? t(`hub.sex.${sex}`) : text;
    }
    if (field === "dateOfBirth") {
      const date = parse(String(text), "MMM d, yyyy", new Date(), { locale: enUS });
      return isValid(date) ? longDate(date) : text;
    }
    return text;
  };

  const course = (startDate, endDate) => {
    const start = isoDate(startDate);
    const end = isoDate(endDate);
    if (start && end) return t("importReview.courseRange", { start: day(start), end: day(end) });
    if (start) return t("importReview.courseStarted", { date: day(start) });
    if (end) return t("importReview.courseUntil", { date: day(end) });
    return "";
  };

  const proposed = (p) => {
    const text = p.proposed;
    const value = p.value || {};
    if (p.id?.startsWith("demographics.")) return demographic(p.field, text);
    if (p.field === "allergies" && text && text === capitalize(value.type)) {
      return SHOWN_ALLERGY_TYPES.includes(value.type) ? t(`hub.allergyType.${value.type}`) : text;
    }
    if (p.field === "pmh" && SHOWN_CONDITION_STATUSES.includes(value.status)) {
      const english = capitalize(value.status);
      if (text === english || text?.startsWith(`${english} · `)) {
        return t(`conditions.statusOptions.${value.status}`) + text.slice(english.length);
      }
      return text;
    }
    if (p.field === "medications") {
      // Rebuilt from the course's parts only when that reproduces the service's text.
      const parts = (courseText) =>
        [value.dosage, value.frequency, courseText, value.notes].filter(Boolean).join(" · ");
      const englishCourse = courseEnglish(value.startDate, value.endDate);
      return englishCourse && parts(englishCourse) === text
        ? parts(course(value.startDate, value.endDate))
        : text;
    }
    return text;
  };

  const current = (p) =>
    p.id?.startsWith("demographics.") ? demographic(p.field, p.current) : p.current;

  // normalizeExtraction adds this one warning itself; the rest are the model's.
  const warning = (text) => {
    const match = END_BEFORE_START.exec(String(text || ""));
    if (!match) return text;
    const [, name, end, start] = match;
    return t("importReview.endBeforeStart", {
      name: name === "A medication" ? t("importReview.aMedication") : name,
      end,
      start,
    });
  };

  return { proposed, current, warning };
};

const END_BEFORE_START =
  /^(.+): the end date \((\d{4}-\d{2}-\d{2})\) is before the start date \((\d{4}-\d{2}-\d{2})\), so the end date was left out\.$/;

// What healthRecordImportService.formatCourse writes, to recognize it in `proposed`.
const courseEnglish = (startDate, endDate) => {
  if ([startDate, endDate].some((d) => d && !isValid(parseISO(String(d))))) return "";
  const day = (iso) => format(parseISO(iso), "d MMM yyyy");
  if (startDate && endDate) return `${day(startDate)} – ${day(endDate)}`;
  if (startDate) return `Started ${day(startDate)}`;
  if (endDate) return `Until ${day(endDate)}`;
  return "";
};

/**
 * Reviews what the extraction found before anything is written.
 *
 * Every row is opt-in. Rows that would overwrite an existing value, or that
 * duplicate a list entry, arrive unchecked and are marked — an uploaded
 * document can belong to a different person, so the patient makes that call
 * rather than the model.
 *
 * With `sourceName` it reviews a record a hospital sent instead of documents.
 */
const ImportFromDocumentsDialog = ({
  open,
  onClose,
  loading,
  error,
  extraction,
  proposals,
  saving,
  onApply,
  sourceName,
}) => {
  const { t } = useTranslation("masterchart");
  const proposalText = useProposalText();
  const [overrides, setOverrides] = useState({});

  // Reset the checkbox overrides whenever a new extraction arrives, so a second
  // run doesn't inherit the previous run's selections.
  const proposalKey = proposals?.map((p) => p.id).join("|") ?? "";
  const [lastKey, setLastKey] = useState(proposalKey);
  if (proposalKey !== lastKey) {
    setLastKey(proposalKey);
    setOverrides({});
  }

  const isSelected = (proposal) =>
    overrides[proposal.id] ?? proposal.selected;

  const toggle = (proposal) =>
    setOverrides((prev) => ({
      ...prev,
      [proposal.id]: !(prev[proposal.id] ?? proposal.selected),
    }));

  const grouped = useMemo(() => {
    const map = new Map();
    (proposals || []).forEach((p) => {
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group).push(p);
    });
    return [...map.entries()];
  }, [proposals]);

  const selectedCount = (proposals || []).filter(isSelected).length;

  const handleApply = () => onApply((proposals || []).filter(isSelected));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("importReview.title")}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              {t("importReview.reading")}
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* One paragraph: whose record this is and what to do. Model
                warnings sit under it, as things to look at while reviewing. */}
            {proposals?.length ? (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {extraction?.demographics?.patientName ? (
                  <>
                    {sourceName
                      ? t("importReview.recordNames", { source: sourceName })
                      : t("importReview.documentsName")}{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {extraction.demographics.patientName}
                    </Box>
                    .{" "}
                  </>
                ) : null}
                {t("importReview.instructions")}
              </Typography>
            ) : null}

            {extraction?.warnings?.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{t("importReview.warningsTitle")}</AlertTitle>
                <Stack spacing={0.75}>
                  {extraction.warnings.map((w, i) => (
                    <Typography variant="body2" key={i}>
                      {proposalText.warning(w)}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            {!proposals?.length ? (
              <Typography variant="body2" color="text.secondary">
                {sourceName
                  ? t("importReview.nothingNewRecord")
                  : t("importReview.nothingNewDocuments")}
              </Typography>
            ) : (
              grouped.map(([group, items], groupIndex) => (
                <Box key={group} sx={{ mt: groupIndex === 0 ? 0 : 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    {t(`importReview.groups.${group}`, { defaultValue: group })}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  {items.map((p) => (
                    <Box key={p.id} sx={{ mb: 1.5 }}>
                      <FormControlLabel
                        sx={{ alignItems: "flex-start", m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={isSelected(p)}
                            onChange={() => toggle(p)}
                            sx={{ pt: 0.25 }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {p.id?.startsWith("demographics.")
                                ? t(`importReview.demographicLabels.${p.field}`, { defaultValue: p.label })
                                : p.label}
                            </Typography>
                            {/* A change reads as old value struck out, then
                                the new one; a duplicate just says so. No
                                chips: the line itself carries the meaning. */}
                            {p.conflict && p.current === ALREADY_IN_RECORD ? (
                              <Typography variant="body2" color="text.secondary">
                                {p.proposed ? `${proposalText.proposed(p)} · ` : ""}
                                {t("importReview.alreadyInMasterChart")}
                              </Typography>
                            ) : p.conflict ? (
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textDecoration: "line-through" }}
                                >
                                  {proposalText.current(p)}
                                </Typography>
                                <ArrowForwardRounded sx={{ fontSize: 16, color: "text.secondary" }} />
                                <Typography variant="body2">{proposalText.proposed(p)}</Typography>
                              </Stack>
                            ) : p.proposed ? (
                              <Typography variant="body2" color="text.secondary">
                                {proposalText.proposed(p)}
                              </Typography>
                            ) : null}
                            {p.sourceDocument ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t("importReview.from", { name: p.sourceDocument })}
                              </Typography>
                            ) : null}
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("importReview.notNow")}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleApply}
          loading={saving}
          disabled={saving || loading || !selectedCount}
        >
          {selectedCount
            ? t("importReview.addCount", { count: selectedCount })
            : t("importReview.add")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImportFromDocumentsDialog;
